// Smart Sash Admin Panel - Fake Hood Usage Data Generator
// Generates realistic fake session data using real entities from stores

import {
  GroupDTO,
  DeviceDTO,
  HoodType,
  HoodUsageSession,
  InstitutionDTO,
  LabDTO,
  UserDTO,
  getComplianceLevel,
} from '@/models';

function normalRandom(mean: number, stdDev: number): number {
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z * stdDev;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomDateWithinDays(days: number): string {
  const now = Date.now();
  const offset = Math.floor(Math.random() * days * 24 * 60 * 60 * 1000);
  return new Date(now - offset).toISOString();
}

function generateId(): string {
  return crypto.randomUUID?.() ?? Math.random().toString(36).substring(2, 15);
}

function formatUserName(user: UserDTO): string {
  const first = user.firstName || 'Unknown';
  const last = user.lastName ? `${user.lastName.charAt(0)}.` : '';
  return `${first} ${last}`.trim();
}

interface HierarchyEntry {
  institutionId: string;
  groupId: string;
  labId: string;
  deviceId: string;
}

function buildHierarchy(
  institutions: InstitutionDTO[],
  groups: GroupDTO[],
  labs: LabDTO[],
  devices: DeviceDTO[]
): HierarchyEntry[] {
  const entries: HierarchyEntry[] = [];

  for (const lab of labs) {
    const group = groups.find((g) => g.id === lab.groupId);
    const institution = group
      ? institutions.find((i) => i.id === group.institutionId)
      : null;

    const labDevices = devices.filter((d) => d.labId === lab.id);
    const deviceId = labDevices.length > 0 ? randomItem(labDevices).id : generateId();

    entries.push({
      institutionId: institution?.id ?? generateId(),
      groupId: group?.id ?? generateId(),
      labId: lab.id,
      deviceId,
    });
  }

  if (entries.length === 0) {
    entries.push({
      institutionId: generateId(),
      groupId: generateId(),
      labId: generateId(),
      deviceId: generateId(),
    });
  }

  return entries;
}

export function generateFakeSessions(
  count: number,
  institutions: InstitutionDTO[],
  groups: GroupDTO[],
  labs: LabDTO[],
  users: UserDTO[],
  devices: DeviceDTO[]
): HoodUsageSession[] {
  const hierarchy = buildHierarchy(institutions, groups, labs, devices);
  const sessions: HoodUsageSession[] = [];

  let vavIndex = 1;
  let cavIndex = 1;

  for (let i = 0; i < count; i++) {
    const isVav = Math.random() < 0.6;
    const hoodType = isVav ? HoodType.VAV : HoodType.CAV;
    const hoodIndex = isVav ? vavIndex++ : cavIndex++;
    const hoodId = `HOOD-${isVav ? 'V' : 'C'}${String(hoodIndex).padStart(2, '0')}`;

    const entry = randomItem(hierarchy);
    const user =
      users.length > 0
        ? randomItem(users)
        : { id: generateId(), firstName: 'Test', lastName: 'User' };

    const duration = Math.round(clamp(normalRandom(60, 30), 15, 180));
    const complianceScore = Math.round(clamp(normalRandom(82, 12), 0, 100));
    const complianceLevel = getComplianceLevel(complianceScore);

    let avgCfm: number | null = null;
    let estEnergyCost: number | null = null;

    if (isVav) {
      avgCfm = Math.round(clamp(normalRandom(450, 150), 200, 900));
      estEnergyCost = Math.round(duration * avgCfm * 0.00015 * 100) / 100;
    }

    sessions.push({
      id: generateId(),
      hoodId,
      hoodType,
      userId: (user as UserDTO).id ?? generateId(),
      userName: formatUserName(user as UserDTO),
      duration,
      complianceScore,
      complianceLevel,
      avgCfm,
      estEnergyCost,
      sessionDate: randomDateWithinDays(90),
      institutionId: entry.institutionId,
      groupId: entry.groupId,
      labId: entry.labId,
      deviceId: entry.deviceId,
    });
  }

  return sessions;
}
